import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap';
import { RecordLink } from 'components/record-chip/RecordLink';
import { getIntl, t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';

const numberFormats = new Map<number, Intl.NumberFormat>();

/**
 * A report figure: in the new design as its locale writes it; in classic a space between thousands and a point before
 * the decimals, as classic's money columns.
 */
export function formatReportNumber(value: number | null | undefined, decimals = 2): string {
    if (value == null) return '';
    if (isNewDesignCopy()) {
        return getIntl().formatNumber(value, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    }
    let format = numberFormats.get(decimals);
    if (!format) {
        format = new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        numberFormats.set(decimals, format);
    }
    return format.format(value).replace(/,/g, ' ');
}

export type ReportTone = 'green' | 'yellow' | 'red';

/**
 * The colours Superset's table gave a figure column: one above zero and, where it had one, one below. Superset
 * coloured only the columns that carried a meaning, such as invoiced in green, billable in yellow, WIP and losses
 * in red, so a column with no rule stays plain.
 */
export type ToneRule = { above?: ReportTone; below?: ReportTone };

export const GREEN: ToneRule = { above: 'green' };
export const YELLOW: ToneRule = { above: 'yellow' };
export const RED: ToneRule = { above: 'red' };
export const GREEN_OR_RED: ToneRule = { above: 'green', below: 'red' };
export const YELLOW_OR_RED: ToneRule = { above: 'yellow', below: 'red' };

/** A pivot's rule: its figure's tone above zero, and red for a loss below it. */
export function pivotToneRule(tone: ReportTone): ToneRule {
    return { above: tone, below: 'red' };
}

/** A figure column's largest value and its smallest, the ends its cells' tones are measured against. */
export type ColumnRange = { max: number; min: number };

export function columnRange<Row>(rows: Row[] | undefined, value: (row: Row) => number | null | undefined): ColumnRange {
    return (rows ?? []).reduce(
        (range, row) => {
            const figure = value(row) ?? 0;
            return { max: Math.max(range.max, figure), min: Math.min(range.min, figure) };
        },
        { max: 0, min: 0 }
    );
}

/** How many steps of strength a tone has; each covers a fifth of the column's largest figure. */
const TONE_STEPS = 5;

/**
 * A figure's cell colour: its rule's tone for its sign, in the step of its size against the column's largest figure
 * of either sign, so the largest figures are the darkest. Zero, an empty cell and a sign the rule gives no tone stay
 * plain.
 */
export function toneBackground(
    value: number | null | undefined,
    rule: ToneRule,
    range: ColumnRange
): string | undefined {
    if (!value) return undefined;
    const tone = value > 0 ? rule.above : rule.below;
    const largest = Math.max(range.max, -range.min);
    if (!tone || !largest) return undefined;
    const step = Math.min(Math.max(Math.ceil((Math.abs(value) / largest) * TONE_STEPS), 1), TONE_STEPS);
    return `var(--report-tone-${tone}-${step})`;
}

type NumberCellProps = {
    value: number | null | undefined;
    decimals?: number;
    /** Colours the whole cell by the column's rule; its column sets {@link TONED_COLUMN_META}. */
    tone?: { rule: ToneRule; range: ColumnRange };
};

/**
 * What a toned figure column puts in its `meta`: no cell padding, as the toned cell draws the padding inside its
 * own colour so the colour fills the cell. The totals cell keeps the table's padding.
 */
export const TONED_COLUMN_META = { padding: 0 } as const;

export function ReportNumberCell({ value, decimals = 2, tone }: NumberCellProps) {
    const figure = <span className="whitespace-nowrap">{formatReportNumber(value, decimals)}</span>;
    if (!tone) {
        return <div className="w-full text-right tabular-nums">{figure}</div>;
    }
    return (
        <div
            className="flex w-full items-center justify-end self-stretch p-4 tabular-nums"
            style={{ backgroundColor: toneBackground(value, tone.rule, tone.range) }}
        >
            {figure}
        </div>
    );
}

/**
 * A toned figure column's cell renderer: each cell coloured by `rule` against the column's range over `rows`. The
 * renderer carries its rule as `toneRule`, so the About panel lists the column under its colours.
 */
export function tonedCell<Row>(
    rows: Row[] | undefined,
    value: (row: Row) => number | null | undefined,
    rule: ToneRule,
    decimals?: number
) {
    const range = columnRange(rows, value);
    function TonedCell({ row }: { row: { original: Row } }) {
        return <ReportNumberCell value={value(row.original)} decimals={decimals} tone={{ rule, range }} />;
    }
    return Object.assign(TonedCell, { toneRule: rule });
}

export function ReportDateCell({ value }: { value: string | null | undefined }) {
    return value ? <DateDefaultNowrap dateValue={value} /> : null;
}

type EntityLinkProps = { children: React.ReactNode; wrapLabel?: boolean };

/**
 * A report value that opens the record it names, in the new design as the record's chip under the value's text; a row
 * without the record shows the value alone.
 */
export function ReportLink({ to, children, wrapLabel }: EntityLinkProps & { to: string | null }) {
    if (to == null) return <>{children}</>;
    return (
        <RecordLink
            to={to}
            label={typeof children === 'string' || typeof children === 'number' ? String(children) : ''}
            className="entity-link text-fg hover:text-link no-underline hover:underline"
            wrapLabel={wrapLabel}
        >
            {children}
        </RecordLink>
    );
}

/** A case number or name that opens the case; a number alone takes `wrapLabel`, as cut it reads like another case's. */
export function ReportCaseLink({ matterId, children, wrapLabel }: EntityLinkProps & { matterId: number | null }) {
    return (
        <ReportLink to={matterId == null ? null : `/matter/${matterId}`} wrapLabel={wrapLabel}>
            {children}
        </ReportLink>
    );
}

/** A client or payer number or name that opens the contact. */
export function ReportContactLink({ partyId, children }: EntityLinkProps & { partyId: number | null }) {
    return <ReportLink to={partyId == null ? null : `/contact/${partyId}`}>{children}</ReportLink>;
}

/** An employee's name, such as a case's supervising partner, that opens the employee. */
export function ReportEmployeeLink({ employeeId, children }: EntityLinkProps & { employeeId: number | null }) {
    return <ReportLink to={employeeHref(employeeId)}>{children}</ReportLink>;
}

/** A core team's name that opens the team. */
export function ReportTeamLink({ teamId, children }: EntityLinkProps & { teamId: number | null }) {
    return <ReportLink to={teamId == null ? null : `/team/${teamId}`}>{children}</ReportLink>;
}

/** An employee's name with the portal's inactive suffix, such as "Jaak Sarv (Inactive)"; an active one has no marker. */
export function employeeLabel(employee: { employeeName: string; isActive: boolean }): string {
    return employee.isActive ? employee.employeeName : `${employee.employeeName}${t('common.inactive-suffix')}`;
}

export function employeeHref(employeeId: number | null): string | null {
    return employeeId == null ? null : `/employee/${employeeId}`;
}
